import { useState, type FormEvent, type ReactNode } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'

import { formatApiErrorMessage } from '../api/apiErrorMessage'
import { deviceLimitDetails } from '../api/apiErrorCodes'
import { deviceTakeoverMessage } from '../billing/paywallCopy'
import { ApiError } from '../api/client'
import { DeviceTakeoverModal } from '../components/billing/DeviceTakeoverModal'
import { useAuth } from '../auth/AuthProvider'
import { isDemoStandEnabled } from '../dev/demoStand'
import { homePathForUser } from '../auth/routing'
import { AuthScreenLayout } from '../components/auth/AuthScreenLayout'
import { EyeIcon, EyeOffIcon, LockIcon, UserIcon } from '../components/auth/AuthIcons'
import {
  hasLoginFormErrors,
  validateLoginForm,
  type LoginFormField,
  type LoginFormFieldErrors,
} from '../utils/loginFormValidation'

export const LoginScreen = () => {
  const { login, session, loading: authLoading, userRoles } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<LoginFormFieldErrors>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [deviceTakeoverText, setDeviceTakeoverText] = useState<string | null>(null)
  const [takeoverLoading, setTakeoverLoading] = useState(false)

  const clearFieldError = (field: LoginFormField) => {
    setFieldErrors((current) => {
      if (!current[field]) {
        return current
      }
      const next = { ...current }
      delete next[field]
      return next
    })
  }

  const focusFirstInvalidField = () => {
    requestAnimationFrame(() => {
      const invalidInput = document.querySelector<HTMLElement>('.market-auth-field--invalid .market-auth-field__input')
      invalidInput?.scrollIntoView({ block: 'center', behavior: 'smooth' })
      invalidInput?.focus()
    })
  }

  if (session) {
    return <Navigate to={homePathForUser(userRoles)} replace />
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    const validationErrors = validateLoginForm({ email, password })
    if (hasLoginFormErrors(validationErrors)) {
      setFieldErrors(validationErrors)
      setFormError(null)
      focusFirstInvalidField()
      return
    }
    setFieldErrors({})
    setLoading(true)
    setFormError(null)
    setDeviceTakeoverText(null)

    try {
      const redirectPath = await login(email.trim(), password)
      navigate(redirectPath, { replace: true })
    } catch (loginError) {
      const deviceLimit = deviceLimitDetails(loginError)
      if (deviceLimit) {
        const lastSeenText = deviceLimit.activeDeviceLastSeen
          ? new Date(deviceLimit.activeDeviceLastSeen).toLocaleString('ru-RU')
          : undefined
        setDeviceTakeoverText(deviceTakeoverMessage(deviceLimit.activeDeviceLabel, lastSeenText))
      } else if (loginError instanceof ApiError && loginError.status === 401) {
        setFormError('Неверный email или пароль')
      } else {
        setFormError(formatApiErrorMessage(loginError, 'Не удалось войти'))
      }
    } finally {
      setLoading(false)
    }
  }

  const handleTakeover = async () => {
    setTakeoverLoading(true)
    setFormError(null)
    try {
      const redirectPath = await login(email.trim(), password, true)
      navigate(redirectPath, { replace: true })
    } catch (takeoverError) {
      setDeviceTakeoverText(null)
      setFormError(formatApiErrorMessage(takeoverError, 'Не удалось войти'))
    } finally {
      setTakeoverLoading(false)
    }
  }

  const handleTogglePassword = () => {
    setShowPassword((current) => !current)
  }

  return (
    <AuthScreenLayout loading={authLoading}>
      <div className="market-auth__card">
        <p className="market-auth__form-logo">Провизиум</p>
        <div className="market-auth__card-header">
          <h2 className="market-auth__card-title">
            {isDemoStandEnabled() ? 'Демо-кабинет' : 'Вход в систему'}
          </h2>
          <p className="market-auth__card-lead">
            {isDemoStandEnabled()
              ? 'Не удалось открыть демо автоматически. Обновите страницу или зарегистрируйтесь на market.provisium.ru'
              : 'Введите логин и пароль от личного кабинета'}
          </p>
        </div>
        <form className="market-auth__form" noValidate onSubmit={(event) => void handleSubmit(event)}>
          <label
            className={`market-auth-field${fieldErrors.email ? ' market-auth-field--invalid' : ''}`}
          >
            <span className="market-auth-field__label">
              Логин или email <span className="market-auth-field__required">*</span>
            </span>
            <span className="market-auth-field__control">
              <UserIcon className="market-auth-field__icon" />
              <input
                className="market-auth-field__input"
                type="text"
                autoComplete="username"
                placeholder="Введите логин или email"
                value={email}
                aria-invalid={fieldErrors.email ? true : undefined}
                aria-describedby={fieldErrors.email ? 'login-email-error' : undefined}
                onChange={(event) => {
                  setEmail(event.target.value)
                  clearFieldError('email')
                }}
              />
            </span>
            {fieldErrors.email ? (
              <span className="market-auth-field__error" id="login-email-error" role="alert">
                {fieldErrors.email}
              </span>
            ) : null}
          </label>

          <label
            className={`market-auth-field${fieldErrors.password ? ' market-auth-field--invalid' : ''}`}
          >
            <span className="market-auth-field__label">
              Пароль <span className="market-auth-field__required">*</span>
            </span>
            <span className="market-auth-field__control">
              <LockIcon className="market-auth-field__icon" />
              <input
                className="market-auth-field__input"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="Введите пароль"
                value={password}
                aria-invalid={fieldErrors.password ? true : undefined}
                aria-describedby={fieldErrors.password ? 'login-password-error' : undefined}
                onChange={(event) => {
                  setPassword(event.target.value)
                  clearFieldError('password')
                }}
              />
              <button
                type="button"
                className="market-auth-field__toggle"
                onClick={handleTogglePassword}
                aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                aria-pressed={showPassword}
              >
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </span>
            {fieldErrors.password ? (
              <span className="market-auth-field__error" id="login-password-error" role="alert">
                {fieldErrors.password}
              </span>
            ) : null}
          </label>

          <DeviceTakeoverModal
            open={deviceTakeoverText != null}
            message={deviceTakeoverText ?? ''}
            loading={takeoverLoading}
            onConfirm={() => void handleTakeover()}
            onDismiss={() => setDeviceTakeoverText(null)}
          />

          {formError ? (
            <p className="echat-error market-auth__error" role="alert">
              {formError}
            </p>
          ) : null}

          <button type="submit" className="echat-btn echat-btn--primary market-auth__submit" disabled={loading}>
            {loading ? 'Вход…' : 'Войти в систему'}
          </button>
        </form>

        <div className="market-auth__footer">
          <Link className="market-auth__link" to="/forgot-password">
            Забыли пароль?
          </Link>
          <p className="market-auth__signup">
            Нет аккаунта?{' '}
            <Link className="market-auth__signup-link" to="/register">
              Регистрация
            </Link>
          </p>
        </div>
      </div>
    </AuthScreenLayout>
  )
}

export const RequireAuth = ({ children }: { children: ReactNode }) => {
  const { session, loading } = useAuth()

  if (loading) {
    return <AuthScreenLayout loading />
  }

  if (!session) {
    return <Navigate to="/login" replace />
  }

  return children
}
